'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function MezoterapiEtkileriNeZamanGorulurPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/mezoterapi-etkileri-ne-zaman-gorulur';
  const pageTitle = 'Mezoterapinin Etkileri Ne Zaman Görülür? - Sonuçlar ve Kalıcılık Rehberi';
  const pageDescription = 'Mezoterapi yaptırdıktan sonra etkiler ne zaman ortaya çıkar? İlk sonuçların görünme süresi, kalıcılık dönemi ve seans aralıkları hakkında tüm detaylar.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Mezoterapi Etkileri Ne Zaman Görülür?', href: '/tr/blog/mezoterapi-etkileri-ne-zaman-gorulur' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Mezoterapi etkileri ve sonuçları değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Mezoterapi Etkileri
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              ✨ Mezoterapinin Etkileri Ne Zaman Görülür?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>23 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Mezoterapi, cildin alt tabakasına mikro enjeksiyonlarla vitamin, mineral, aminoasit ve hyaluronik asit gibi aktif maddelerin verilmesiyle yapılan bir tedavi yöntemidir. Peki, bu işlemden sonra etkiler ne kadar sürede görülür? Aslında cevap kişiye, uygulama bölgesine ve kullanılan içeriklere göre değişir. Ancak genel olarak, mezoterapi etkileri 1 ila 3 hafta içinde fark edilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. İlk Etkilerin Görülme Süresi
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                İlk seanstan sonra ciltte gözle görülür değişiklikler genellikle 7–10 gün içinde başlar. Bu süreçte:
              </p>
              
              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Cilt daha parlak ve canlı görünür</li>
                  <li>Hafif nem artışı fark edilir</li>
                  <li>İnce çizgilerde azalma hissedilir</li>
                </ul>
                <p className="text-green-800 mt-3">
                  Eğer uygulama saç derisine yapılmışsa, ilk etkiler 2–3 hafta içinde dökülmenin azalması şeklinde görülür.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Kalıcı Etkinin Ortaya Çıkması
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Kalıcılık, tek bir seansla değil; düzenli uygulamalarla sağlanır. Genellikle 3–4 seans sonrası cilt elastikiyetinde belirgin iyileşme olur. Tam etki 6–8 hafta içinde maksimum düzeye ulaşır.
              </p>

              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Seans Sayısı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etki Düzeyi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">1. Seans</td>
                      <td className="px-6 py-4 text-sm text-gray-900">%30</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Parlaklık, nem artışı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">3. Seans</td>
                      <td className="px-6 py-4 text-sm text-gray-900">%70</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cilt dokusu yenilenir</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">5. Seans ve sonrası</td>
                      <td className="px-6 py-4 text-sm text-gray-900">%100</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kollajen üretimi artar, cilt sıkılaşır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Etki Süresini Uzatan Faktörler
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Mezoterapinin kalıcılığını artırmak için şu noktalara dikkat edilmelidir:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>İşlem sonrası ilk 24 saat cilt yıkanmamalıdır</li>
                  <li>Güneşten korunma çok önemlidir (SPF 30+)</li>
                  <li>Yeterli su tüketimi sağlanmalıdır</li>
                  <li>Düzenli uyku ve dengeli beslenme alışkanlıkları sürdürülmelidir</li>
                  <li>Cilt bakım ürünleri (serum, nemlendirici) düzenli kullanılmalıdır</li>
                </ul>
                <p className="text-blue-800 mt-3 font-medium">
                  Bu önlemlerle etki süresi 6 aya kadar uzayabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Saç Mezoterapisinde Etki Zamanı
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Saç dökülmesi tedavisinde uygulanan mezoterapi, farklı bir döngüde çalışır:
              </p>

              <div className="space-y-4 mb-8">
                <div className="bg-emerald-50 rounded-lg p-6">
                  <h3 className="font-semibold text-emerald-900 mb-2">İlk 2 hafta:</h3>
                  <p className="text-emerald-800">Dökülme azalır</p>
                </div>

                <div className="bg-emerald-50 rounded-lg p-6">
                  <h3 className="font-semibold text-emerald-900 mb-2">3.–4. hafta:</h3>
                  <p className="text-emerald-800">Yeni saç kökleri güçlenmeye başlar</p>
                </div>

                <div className="bg-emerald-50 rounded-lg p-6">
                  <h3 className="font-semibold text-emerald-900 mb-2">2. ay:</h3>
                  <p className="text-emerald-800">Saç yoğunluğu ve kalınlığı gözle görülür biçimde artar</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Bu süreç, sabır gerektirir ancak düzenli seanslarla sonuç oldukça tatmin edicidir.
              </p>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Mezoterapi etkileri değerlendirmesi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Mezoterapiden sonra etki hemen görülür mü?</h3>
                  <p className="text-gray-700">Hayır, genellikle ilk etkiler 1 hafta içinde başlar, tam sonuçlar birkaç seans sonrası ortaya çıkar.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Kaç seans mezoterapi yapılmalıdır?</h3>
                  <p className="text-gray-700">Ortalama 4–6 seans önerilir; aralıklar 2–3 haftadır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Kalıcı sonuç için bakım gerekir mi?</h3>
                  <p className="text-gray-700">Evet, cilt yenilenmesi devam eden bir süreçtir. 6 ayda bir idame seans önerilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Mezoterapi sonrası morluk normal mi?</h3>
                  <p className="text-gray-700">Evet, mikro iğneler nedeniyle geçici kızarıklık veya morluk olabilir, birkaç gün içinde kaybolur.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Sonuç herkeste aynı mı olur?</h3>
                  <p className="text-gray-700">Hayır, yaş, cilt tipi, yaşam tarzı ve kullanılan ürün kalitesi sonucu etkiler.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>European Journal of Aesthetic Medicine – "Clinical Efficacy of Mesotherapy"</li>
                <li>Türk Dermatoloji Derneği – Mezoterapi Uygulama Kılavuzu</li>
                <li>Harvard Health Publishing – "Non-surgical Skin Rejuvenation Techniques"</li>
                <li>Mayo Clinic – "How Long Do Mesotherapy Results Last?"</li>
                <li>Michel Pistor (1952) – "La mésothérapie: une nouvelle technique thérapeutique"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#mezoterapi</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#ciltbakımı</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#saçmezoterapisi</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#güzellik</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#ciltgençleştirme</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Mezoterapi Uygulaması İçin Randevu Alın</h3>
              <p className="mb-6">Cilt gençleştirme ve saç dökülmesi tedavisi için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Mezoterapi uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/botoks-ne-kadar-sure-gider"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Ne Kadar Süre Gider?</h4>
              <p className="text-gray-600 text-sm">Botoks etki süresi ve kalıcılık hakkında detaylı bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-fiyatlari-neden-farkli"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları Neden Farklı?</h4>
              <p className="text-gray-600 text-sm">Estetik uygulamalarda fiyat farklılıklarının nedenleri.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
